'use client';
import { useMemo } from 'react';
import {
  Button,
  Tile,
  TileContent,
  TileTitle,
  TilePrice,
  TileDetails,
  TileStatus,
  TileMeta,
  TileReservedBanner
} from '@paction-2.0/ui';
import Link from 'next/link';
import { OptimizedTileImage } from '@/components/tiles';
import type {
  CatalogProviderLoaderResult,
  CatalogProviderSummary,
  NormalizedCatalogSearchFilters
} from '@/features/catalog';
import { getListingUrl, getWaitlistUrl } from '@paction-2.0/util';
import type { UsePuppySearchResult } from '../usePuppySearch';
import { usePuppySearchResults } from './usePuppySearchResults';
import { formatListingAvailabilityStatus } from './listingAvailabilityStatus';
import styles from './styles/puppySearchResults.module.scss';

type PuppySearchResultsProps = {
  providers: CatalogProviderSummary[];
  pagination: CatalogProviderLoaderResult['pagination'];
  filters: NormalizedCatalogSearchFilters;
  status: UsePuppySearchResult['status'];
  onLoadMore: () => Promise<void>;
  selectedProviderId: number | null;
  onSelectProvider: (providerId: number | null) => void;
};

type ListingItem = {
  type: 'listing';
  id: number;
  title: string;
  slug: string | null;
  shortId: string | null;
  breed: string | null;
  breedSlug: string | null;
  price: number | null;
  isAvailableNow: boolean | null;
  availableDate: string | null;
  isRehomed: boolean | null;
  isReserved: boolean;
  imageUrl: string | null;
  sex: string | null;
  provider: CatalogProviderSummary;
};

type WaitlistItem = {
  type: 'waitlist';
  id: number;
  title: string;
  slug: string | null;
  shortId: string | null;
  breed: string | null;
  breedSlug: string | null;
  priceMin: number | null;
  priceMax: number | null;
  estimatedDateOfBirth: string | null;
  imageUrl: string | null;
  provider: CatalogProviderSummary;
};

type TileItem = ListingItem | WaitlistItem;

const flattenToTiles = (providers: CatalogProviderSummary[]): TileItem[] => {
  const items: TileItem[] = [];

  for (const provider of providers) {
    for (const listing of provider.listings) {
      items.push({
        type: 'listing',
        id: listing.id,
        title: listing.title ?? 'Unnamed Listing',
        slug: listing.slug,
        shortId: listing.shortId,
        breed: listing.breed,
        breedSlug: listing.breedSlug,
        price: listing.price,
        isAvailableNow: listing.isAvailableNow,
        availableDate: listing.availableDate,
        isRehomed: listing.isRehomed,
        isReserved: listing.isReserved,
        imageUrl: listing.imageUrl,
        sex: listing.sex,
        provider
      });
    }

    for (const waitlist of provider.waitlists) {
      items.push({
        type: 'waitlist',
        id: waitlist.id,
        title: waitlist.title ?? 'Unnamed Waitlist',
        slug: waitlist.slug,
        shortId: waitlist.shortId,
        breed: waitlist.breed,
        breedSlug: waitlist.breedSlug,
        priceMin: waitlist.priceMin,
        priceMax: waitlist.priceMax,
        estimatedDateOfBirth: waitlist.estimatedDateOfBirth,
        imageUrl: waitlist.imageUrl,
        provider
      });
    }
  }

  return items;
};

const getProvinceShort = (province: string | null): string | null => {
  if (!province) return null;
  const map: Record<string, string> = {
    'british-columbia': 'BC',
    ontario: 'ON',
    alberta: 'AB',
    quebec: 'QC',
    saskatchewan: 'SK',
    manitoba: 'MB',
    'nova-scotia': 'NS',
    'new-brunswick': 'NB',
    'newfoundland-and-labrador': 'NL',
    'prince-edward-island': 'PE',
    'northwest-territories': 'NT',
    yukon: 'YT',
    nunavut: 'NU'
  };
  return map[province.toLowerCase()] ?? province;
};

const formatGender = (sex: string | null): 'male' | 'female' | null => {
  if (!sex) return null;
  const lowered = sex.toLowerCase();
  if (lowered === 'male') return 'male';
  if (lowered === 'female') return 'female';
  return null;
};

const formatEstimatedDate = (dateString: string | null): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  const day = date.getUTCDate();
  let period: string;
  if (day <= 10) {
    period = 'Early';
  } else if (day <= 20) {
    period = 'Mid';
  } else {
    period = 'Late';
  }

  const monthYear = date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });

  return `${period} ${monthYear}`;
};

export function PuppySearchResults({
  providers,
  pagination,
  filters,
  status,
  onLoadMore,
  selectedProviderId,
  onSelectProvider
}: PuppySearchResultsProps) {
  const { hasResults, showLoadMore, progressLabel, pageLabel } =
    usePuppySearchResults({
      providers,
      pagination,
      status,
      filters
    });

  const tiles = useMemo(
    () =>
      flattenToTiles(providers).sort((a, b) => {
        const aRehomed = a.type === 'listing' && a.isRehomed;
        const bRehomed = b.type === 'listing' && b.isRehomed;
        return aRehomed === bRehomed ? 0 : aRehomed ? 1 : -1;
      }),
    [providers]
  );

  const showResults = hasResults && tiles.length > 0;

  return (
    <section className={styles.resultsSection} aria-live="polite">
      {status.isLoading && (
        <div className={styles.resultsHeader}>
          <span className={styles.statusPill}>Updating...</span>
        </div>
      )}

      {status.error && (
        <div className={styles.errorBanner} role="alert">
          <p>We could not refresh the search results. Please try again.</p>
        </div>
      )}

      {showResults ? (
        <>
          <h2 className="ds-sr-only">Available puppies</h2>
          <div className={styles.tileGrid}>
            {tiles.map((item, index) => {
              const isSelected = item.provider.id === selectedProviderId;
              const handleSelect = () => onSelectProvider(item.provider.id);
              const isPriority = index < 4;

              if (item.type === 'listing') {
                const { label: statusLabel, variant: statusVariant } =
                  formatListingAvailabilityStatus(item);
                const listingUrl = getListingUrl({
                  slug: item.slug,
                  shortId: item.shortId,
                  breed: item.breed,
                  provider: {
                    slug: item.provider.slug,
                    address: item.provider.provinceSlug
                      ? { state: item.provider.provinceSlug }
                      : undefined
                  }
                });

                return (
                  <Tile
                    key={`listing-${item.id}`}
                    itemType="listing"
                    className={styles.tile}
                    ariaLabel={item.title}
                    onMouseEnter={handleSelect}
                    onFocus={handleSelect}
                    data-selected={isSelected || undefined}
                    renderLink={
                      listingUrl
                        ? (props) => (
                            <Link href={listingUrl} {...props}>
                              {props.children}
                            </Link>
                          )
                        : undefined
                    }
                  >
                    <OptimizedTileImage
                      imageUrl={item.imageUrl ?? item.provider.imageUrl}
                      alt={item.title}
                      priority={isPriority}
                      overlay={
                        (item.isRehomed || item.isReserved) && (
                          <TileReservedBanner text="Reserved" />
                        )
                      }
                    />
                    <TileContent>
                      <TileTitle>{item.title}</TileTitle>
                      <TilePrice price={item.price} />
                      <TileDetails
                        breed={item.breed}
                        gender={formatGender(item.sex)}
                        city={item.provider.city}
                        province={getProvinceShort(item.provider.provinceSlug)}
                      />
                      <TileStatus
                        status={statusLabel}
                        variant={statusVariant}
                      />
                    </TileContent>
                  </Tile>
                );
              }

              // Waitlist
              const waitlistUrl = getWaitlistUrl({
                slug: item.slug,
                shortId: item.shortId,
                breed: item.breed,
                provider: {
                  slug: item.provider.slug,
                  address: item.provider.provinceSlug
                    ? { state: item.provider.provinceSlug }
                    : undefined
                }
              });

              return (
                <Tile
                  key={`waitlist-${item.id}`}
                  itemType="waitlist"
                  className={styles.tile}
                  ariaLabel={item.title}
                  onMouseEnter={handleSelect}
                  onFocus={handleSelect}
                  data-selected={isSelected || undefined}
                  renderLink={
                    waitlistUrl
                      ? (props) => (
                          <Link href={waitlistUrl} {...props}>
                            {props.children}
                          </Link>
                        )
                      : undefined
                  }
                >
                  <OptimizedTileImage
                    imageUrl={item.imageUrl ?? item.provider.imageUrl}
                    alt={item.title}
                    priority={isPriority}
                  />
                  <TileContent>
                    <TileTitle>{item.title}</TileTitle>
                    {formatEstimatedDate(item.estimatedDateOfBirth) && (
                      <TileMeta>
                        {formatEstimatedDate(item.estimatedDateOfBirth)}
                      </TileMeta>
                    )}
                    <TileDetails
                      breed={item.breed}
                      city={item.provider.city}
                      province={getProvinceShort(item.provider.provinceSlug)}
                    />
                    <TileStatus status="Waitlist open" variant="blue" />
                  </TileContent>
                </Tile>
              );
            })}
          </div>

          <div className={styles.loadMoreRow}>
            {showLoadMore && (
              <Button
                variant="primary"
                onClick={onLoadMore}
                disabled={status.isLoadingMore}
              >
                {status.isLoadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
            <p className={styles.paginationMeta}>
              {progressLabel}
              {pageLabel ? ` · ${pageLabel}` : ''}
            </p>
          </div>
        </>
      ) : null}
    </section>
  );
}
